import React from "react";
import {
  DocumentUpdateActions,
  EvidenceRequestDocument,
  EvidenceRequestUpdatePayload,
  InvestorEvidenceRequestDetail,
  RequiredEvidenceDocumentUpdatePayload,
} from "../api/transformers/requiredEvidence";
import {
  EvidenceCategory,
  EvidenceRequestStatus,
  EvidenceRequestStatusEnum,
  EvidenceRequestStatusNaming,
  MAX_EVIDENCE_NUM_PER_REQUEST,
} from "../constant/requiredEvidence";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Button,
  TextField,
  MenuItem,
  FormControl,
  Box,
  FormHelperText,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import useBoolean from "../hooks/useBoolean";
import useInvestorAmlDetailStore from "../zstore/investorAmlDetail";
import { useFormik } from "formik";
import useRequiredEvidenceTypesStore from "../zstore/requiredEvidenceTypes";
import { useShallow } from "zustand/react/shallow";
import { Option, RawFile, SavedFile } from "../model";
import { keys, map } from "lodash";
import Text from "../components/Text";
import DocumentList, { DocumentListRef } from "./AmlDocumentList";
import { fullName } from "../utils/investor";
import { RqdEvidenceAPI } from "../api";
import useMessage from "../hooks/useMessage";

type EvidenceRequestDetailsModalProps = {
  open: boolean;
  personId: string;
  data: InvestorEvidenceRequestDetail | null;
  onCancel: () => void;
  onSuccess: (
    status: EvidenceRequestStatusEnum,
    evidenceRequestCode: string
  ) => void;
  onPreconditionCheck: (
    status: EvidenceRequestStatusEnum,
    newStatus: EvidenceRequestStatusEnum
  ) => boolean;
};

type FormikValues = {
  evidenceStatus: string;
  evidenceTypeCode: string;
  reviewerComment: string;
  submitterComment: string;
};
const statusOptions = getStatusOptions();
const EvidenceRequestDetailsModal: React.FC<
  EvidenceRequestDetailsModalProps
> = ({ open, personId, data, onCancel, onSuccess, onPreconditionCheck }) => {
  const [submitting, { setTrue: startSubmitting, setFalse: finishSubmitting }] =
    useBoolean();
  const investorDetail = useInvestorAmlDetailStore((state) => state.data);
  const { getType } = useRequiredEvidenceTypesStore(personId);
  const message = useMessage();
  const documentListRef = React.useRef<DocumentListRef>(null);
  const formik = useFormik<FormikValues>({
    initialValues: toFormikValues(data),
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });
  React.useEffect(() => {
    if (data) {
      formik.setValues(toFormikValues(data));
    }
  }, [data]);
  if (!data) {
    return null;
  }
  const evidenceRequestType = getType(data.code)!;
  const documentDataToModel = (doc: EvidenceRequestDocument): SavedFile => {
    return {
      urn: doc.urn,
      name: doc.filename,
      uploader: doc.createdByUser,
      uploadTime: doc.updatedOnDate,
      amlCategoryCode: doc.amlTypeCode,
      amlCategoryDescription: doc.amlTypeDesc,
      otherTypeDescription: doc.otherTypeDesc,
    };
  };
  const documentModelToData = (
    model: SavedFile & { action?: DocumentUpdateActions }
  ): RequiredEvidenceDocumentUpdatePayload => {
    return {
      documentURN: model.urn,
      documentName: model.name,
      category: model.amlCategoryCode,
      otherTypeDescription: model.otherTypeDescription,
      action: model.action,
    };
  };
  const handleSubmit = async (values: FormikValues) => {
    const payload: EvidenceRequestUpdatePayload = {
      evidenceStatus: values.evidenceStatus,
      evidenceTypeCode: values.evidenceTypeCode,
      reviewerComment: values.reviewerComment,
      submitterComment: values.submitterComment,
      documents: [],
    };
    if (
      onPreconditionCheck &&
      !onPreconditionCheck(
        data.status as EvidenceRequestStatusEnum,
        values.evidenceStatus as EvidenceRequestStatusEnum
      )
    ) {
      return;
    }
    try {
      startSubmitting();
      // if current evidence request for document then build documents array.
      if (
        evidenceRequestType.attachmentCategory === EvidenceCategory.DOCUMENT &&
        documentListRef.current
      ) {
        const currentDocuments = await documentListRef.current.uploadAll();
        payload.documents = mergeDocumentWithAction(
          data.documents,
          currentDocuments
        ).map(documentModelToData);
      }
      await RqdEvidenceAPI.updateEvidenceRequest(personId, payload);
      finishSubmitting();
      message.success("Evidence request updated.");
      onSuccess &&
        onSuccess(
          values.evidenceStatus as EvidenceRequestStatusEnum,
          data.code
        );
    } catch (err) {
      message.error("Failed to update evidence request.");
      console.error(err);
      finishSubmitting();
    }
  };
  const renderAttachedDocuments = () => {
    const { documents } = data;
    const initialDocs = documents.map(documentDataToModel);
    return (
      <DocumentList
        disabled={submitting}
        ref={documentListRef}
        removable
        initialDocs={initialDocs}
        maxDocNumber={MAX_EVIDENCE_NUM_PER_REQUEST}
        allowUpload
        UploadModalProps={{
          investorId: personId,
          fileUsage: `Upload AML documents for: ${fullName(investorDetail!)} (${
            investorDetail!.personId
          })`,
          categoryOptions: evidenceRequestType.documentCategories.map((el) => ({
            value: el.code,
            label: el.description,
            docVaultCategory: el.docVaultCategory,
          })),
        }}
      />
    );
  };
  const showSubmitterComments =
    data.attachmentCategory === EvidenceCategory.TEXT ||
    Boolean(data.submitterComment);
  const showDocuments =
    data.attachmentCategory === EvidenceCategory.DOCUMENT ||
    (Boolean(data.documents) && data.documents.length > 0);
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      fullWidth
      PaperProps={{
        sx: {
          p: 0,
        },
      }}
    >
      <DialogTitle sx={{ fontSize: "1.2rem" }}>
        Required Evidence Detail
      </DialogTitle>
      <DialogContent>
        <Stack
          direction="column"
          rowGap="12px"
          sx={{ width: "100%", marginTop: "10px" }}
        >
          <TextField
            size="small"
            label="Type of Evidence *"
            name="evidenceTypeCode"
            value={evidenceRequestType.description}
            disabled
          />
          <TextField
            size="small"
            label="Status *"
            name="evidenceStatus"
            select
            value={formik.values.evidenceStatus}
            disabled={submitting}
            onChange={formik.handleChange}
            error={
              formik.touched.evidenceStatus &&
              Boolean(formik.errors.evidenceStatus)
            }
            helperText={
              formik.touched.evidenceStatus && formik.errors.evidenceStatus
            }
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                    p: 0,
                  },
                },
              },
            }}
          >
            {statusOptions.map((el) => (
              <MenuItem value={el.value}>{el.label}</MenuItem>
            ))}
          </TextField>
          {showDocuments && (
            <Stack direction="column" rowGap="4px">
              <Text>Attached Documents</Text>
              <div>{renderAttachedDocuments()}</div>
            </Stack>
          )}
          {showSubmitterComments && (
            <FormControl fullWidth>
              <TextField
                label="Submitter Comments"
                name="submitterComment"
                multiline
                disabled={submitting}
                value={formik.values.submitterComment}
                rows={3}
                placeholder="You can write a note here"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={
                  formik.touched.submitterComment &&
                  Boolean(formik.errors.submitterComment)
                }
                helperText={
                  formik.touched.submitterComment &&
                  formik.errors.submitterComment
                }
                inputProps={{ maxLength: 200 }}
              />
              <Box display="flex" justifyContent="flex-end">
                <FormHelperText>{`${
                  formik.values.submitterComment.length
                }/${200}`}</FormHelperText>
              </Box>
            </FormControl>
          )}
          <FormControl fullWidth>
            <TextField
              label="Reviewer Comments"
              name="reviewerComment"
              multiline
              value={formik.values.reviewerComment}
              disabled={submitting}
              rows={3}
              placeholder="You can write a note here"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              error={
                formik.touched.reviewerComment &&
                Boolean(formik.errors.reviewerComment)
              }
              helperText={
                formik.touched.reviewerComment && formik.errors.reviewerComment
              }
              inputProps={{ maxLength: 200 }}
            />
            <Box display="flex" justifyContent="flex-end">
              <FormHelperText>{`${
                formik.values.reviewerComment.length
              }/${200}`}</FormHelperText>
            </Box>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" columnGap="10px">
          <Button
            variant="text"
            sx={{
              backgroundColor: "transparent",
              color: "primary.main",
              padding: "6px 8px",
              minWidth: "64px",
              fontSize: "0.875rem",
              fontWeight: 500,
              lineHeight: 1.75,
              borderRadius: "4px",
              textTransform: "uppercase",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "rgba(25, 118, 210, 0.04)",
                boxShadow: "none",
              },
            }}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color="primary"
            sx={{ borderRadius: "4px" }}
            loading={submitting}
            onClick={formik.submitForm}
          >
            Save
          </LoadingButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

function toFormikValues(
  data: InvestorEvidenceRequestDetail | null
): FormikValues {
  return {
    evidenceStatus: data?.status || EvidenceRequestStatus.NRQ,
    evidenceTypeCode: data?.code || "",
    reviewerComment: data?.reviewerComment || "",
    submitterComment: data?.submitterComment || "",
  };
}

function getStatusOptions(): Option[] {
  return map(keys(EvidenceRequestStatus), (status) => {
    return {
      value: status,
      label: EvidenceRequestStatusNaming[status as EvidenceRequestStatusEnum],
    };
  });
}

function mergeDocumentWithAction(
  original: EvidenceRequestDocument[],
  current: SavedFile[]
) {
  const final: (SavedFile & { action?: DocumentUpdateActions })[] = [];
  const addToFinal = (doc: SavedFile, action?: DocumentUpdateActions) => {
    const exist = final.some((el) => el.urn === doc.urn);
    if (!exist) {
      final.push({
        ...doc,
        action,
      });
    }
  };
  const originalUIDSet = new Set(original.map((el) => el.urn));
  const currentUIDSet = new Set(current.map((el) => el.urn));
  original.forEach((doc) => {
    const savedFile: SavedFile = {
      urn: doc.urn,
      name: doc.filename,
      uploader: doc.createdByUser,
      uploadTime: doc.updatedOnDate,
      amlCategoryCode: doc.amlTypeCode,
      amlCategoryDescription: doc.amlTypeDesc,
      otherTypeDescription: doc.otherTypeDesc,
    };
    // no actions
    if (currentUIDSet.has(doc.urn)) {
      addToFinal(savedFile);
    } else {
      // delete those in original but not in current.
      addToFinal(savedFile, "delete");
    }
  });
  current.forEach((doc) => {
    // add those not exist in original.
    if (!originalUIDSet.has(doc.urn)) {
      addToFinal(doc, "add");
    }
  });
  return final;
}

export default EvidenceRequestDetailsModal;
